import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiMessageSquare, FiStar, FiFilter, FiSearch, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { getReviews, approveReview, replyToReview, deleteReview } from '../services/reviews';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import ReplyModal from '../components/Reviews/ReplyModal';
import toast from 'react-hot-toast';

const targetTypeLabels = {
  service: 'Service',
  driver: 'Driver',
  tour_guide: 'Tour Guide',
  tour_company: 'Tour Company'
};

const targetTypeColors = {
  service: 'bg-blue-100 text-blue-800',
  driver: 'bg-green-100 text-green-800',
  tour_guide: 'bg-purple-100 text-purple-800',
  tour_company: 'bg-orange-100 text-orange-800'
};

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', target_type: '', rating: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, [filters]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const data = await getReviews(filters);
      setReviews(data.data || []);
      setStats(data.stats);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveReview(id);
      toast.success('Review approved');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to approve review');
    }
  };

  const handleReply = async (id, reply) => {
    try {
      await replyToReview(id, reply);
      toast.success('Reply added');
      setReplyModalOpen(false);
      fetchReviews();
    } catch (error) {
      toast.error('Failed to add reply');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await deleteReview(id);
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Reviews Management</h1>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Total Reviews</p>
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Approved</p>
            <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-500">Average Rating</p>
            <p className="text-2xl font-bold text-primary-600">{stats.average_rating?.toFixed(1)} ★</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Target Type</label>
            <select
              value={filters.target_type}
              onChange={(e) => setFilters({ ...filters, target_type: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="service">Services</option>
              <option value="driver">Drivers</option>
              <option value="tour_guide">Tour Guides</option>
              <option value="tour_company">Tour Companies</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Rating</label>
            <select
              value={filters.rating}
              onChange={(e) => setFilters({ ...filters, rating: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">All</option>
              <option value="5">5 ★</option>
              <option value="4">4 ★ & up</option>
              <option value="3">3 ★ & up</option>
              <option value="2">2 ★ & up</option>
              <option value="1">1 ★ & up</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Customer name"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reviews.map((review) => (
                  <tr key={review._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium">{review.user_name}</p>
                      <p className="text-xs text-gray-500">{review.user_email || review.user_phone}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${targetTypeColors[review.target_type]}`}>
                        {targetTypeLabels[review.target_type]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {renderStars(review.rating)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium">{review.title}</p>
                      <p className="text-sm text-gray-600 truncate max-w-xs">{review.comment}</p>
                      {review.admin_reply && (
                        <p className="text-xs text-primary-600 mt-1 flex items-center">
                          <FiMessageSquare className="mr-1" /> Replied: {review.admin_reply.text}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">{formatDate(review.created_at)}</td>
                    <td className="px-6 py-4">
                      {review.is_approved ? (
                        <span className="flex items-center text-green-600 text-sm">
                          <FiThumbsUp className="mr-1" /> Approved
                        </span>
                      ) : (
                        <span className="flex items-center text-yellow-600 text-sm">
                          <FiThumbsDown className="mr-1" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!review.is_approved && (
                        <button
                          onClick={() => handleApprove(review._id)}
                          className="text-green-500 hover:text-green-700 mr-3"
                          title="Approve"
                        >
                          <FiCheck className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedReview(review);
                          setReplyModalOpen(true);
                        }}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                        title="Reply"
                      >
                        <FiMessageSquare className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(review._id)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <FiX className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {reviews.length === 0 && (
            <div className="text-center py-12 text-gray-500">No reviews found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Reply Modal */}
      <ReplyModal
        isOpen={replyModalOpen}
        onClose={() => {
          setReplyModalOpen(false);
          setSelectedReview(null);
        }}
        onReply={(reply) => handleReply(selectedReview?._id, reply)}
        review={selectedReview}
      />
    </div>
  );
}
